import * as z from 'zod';

import { EVENT_TYPE, MAIN_IDENTIFIER_TYPE } from 'src/features/eventLog/types';
import {
    ALIGN,
    COMMUNICATOR_TYPE,
    FISCAL_DEVICE_TYPE,
    GATEWAY_DISPLAY_MODEL,
    IDLE_STATE,
    LISTEN_STATE,
    MEMBERSHIP_CATEGORY,
    ORIENTATION,
    POSITION,
    SYSTEM_COMPONENT,
    SYSTEM_SETTINGS_CATEGORY,
    SYSTEM_SETTINGS_CODE,
    TICKET_PRINTER_ROW_TYPE,
} from 'src/features/systems/utils/types';
import { validationMessagesMap } from 'src/utils/messages';

/**
 * Components whose zone is never asked for. A gateway sits between two zones, so there is no
 * correct answer to give the field; the backend derives the site from the gateway instead.
 */
export const COMPONENTS_WITHOUT_ZONE: readonly string[] = [
    SYSTEM_COMPONENT.GATEWAY_COMMUNICATOR,
    SYSTEM_COMPONENT.SENSOR_READER_COMMUNICATOR,
    SYSTEM_COMPONENT.GATEWAY_DISPLAY,
];

export type SystemSettingCreateFormSchemaValues = z.infer<typeof systemSettingCreateFormSchema>;
export type SystemSettingFormSchemaValues = z.infer<typeof formSchema>;

export const systemSettingCreateFormSchema = z.object({
    category: z.enum(SYSTEM_SETTINGS_CATEGORY),
    code: z.enum(SYSTEM_SETTINGS_CODE),
    value: z.union([
        z.array(z.enum(MAIN_IDENTIFIER_TYPE)).min(1),
        z.enum(MAIN_IDENTIFIER_TYPE),
        z.array(z.enum(MEMBERSHIP_CATEGORY)).min(1),
        z.array(z.enum(EVENT_TYPE)).min(1),
    ]),
});

export const formSchema = z.object({
    supported_identifier_type: z.array(z.enum(MAIN_IDENTIFIER_TYPE)).min(1),
    main_identifier_type: z.enum(MAIN_IDENTIFIER_TYPE),
    enabled_membership_categories: z.array(z.enum(MEMBERSHIP_CATEGORY)).min(1),
    enabled_event_types: z.array(z.enum(EVENT_TYPE)).min(1),
    auto_end_active_stay_after_hours: z.union([z.number().int().min(0), z.literal('')]),
});

export type SystemComponentFormSchemaValues = z.infer<typeof systemComponentFormSchema>;

const commonComponentDataSchema = z.object({
    type: z.string().nonempty(),
    make: z.string().nonempty(),
    model: z.string().nonempty(),
    serial_number: z.string().nonempty(),
});

const cameraComponentDataSchema = z
    .object({
        rtsp_stream_url: z.url({ message: validationMessagesMap.validationErrorURL }),
    })
    .and(commonComponentDataSchema);

const communicatorDataSchema = z
    .object({
        channel: z.number(),
        topic_code: z.string().optional(),
    })
    .and(commonComponentDataSchema);

// persistent_channel, status_channel and status_listen_state are all optional: the first only
// exists on barriers wired with a permanent-open relay, and the latter two only on ones wired to
// report their state. Requiring them forced installers to invent values, which made gateways
// report a barrier state they could not actually observe.
const gatewayCommunicatorDataSchema = z
    .object({
        persistent_channel: z.number().nullable().optional(),
        open_duration: z.number(),
        status_listen_state: z.enum(LISTEN_STATE).optional(),
        status_channel: z.int().nullable().optional(),
    })
    .and(communicatorDataSchema);

const sensorReaderCommunicatorDataSchema = z
    .object({
        listen_state: z.enum(LISTEN_STATE).optional(),
        position: z.enum(POSITION),
    })
    .and(communicatorDataSchema)
    .superRefine((data, ctx) => {
        if (data.type === COMMUNICATOR_TYPE.SP_MQTT && !data.listen_state) {
            ctx.addIssue({
                path: ['listen_state'],
                message: validationMessagesMap.validationErrorRequired,
                code: 'custom',
            });
        }
    });

const gatewayDisplayDataSchema = z
    .object({
        display_server_ip: z.string().optional(),
        display_server_api_key: z.string().optional(),
        display_width: z.int().optional(),
        display_height: z.int().optional(),
        display_title_font_size: z.int().optional(),
        display_subtitle_font_size: z.int().optional(),
        display_title_only_font_size: z.int().optional(),
        idle_state: z.enum(IDLE_STATE).optional(),
        orientation: z.enum(ORIENTATION).optional(),
        display_model: z.enum(GATEWAY_DISPLAY_MODEL),
    })
    .superRefine((data, ctx) => {
        type Data = typeof data;

        const requiredFieldsByModel: Record<string, (keyof Data)[]> = {
            [GATEWAY_DISPLAY_MODEL.SP]: ['display_server_ip', 'display_server_api_key', 'idle_state', 'orientation'],
            [GATEWAY_DISPLAY_MODEL.HUIDU]: [
                'display_server_ip',
                'display_width',
                'display_height',
                'display_title_font_size',
                'display_subtitle_font_size',
                'display_title_only_font_size',
            ],
            [GATEWAY_DISPLAY_MODEL.NED200]: ['display_server_ip'],
            [GATEWAY_DISPLAY_MODEL.NED200C]: ['display_server_ip'],
        };

        const requiredFields = requiredFieldsByModel[data.display_model] || [];

        requiredFields.forEach((field) => {
            if (!data[field]) {
                ctx.addIssue({
                    path: [field],
                    message: validationMessagesMap.validationErrorRequired,
                    code: 'custom',
                });
            }
        });
    });

const fiscalDeviceDataSchema = z.object({
    type: z.enum(FISCAL_DEVICE_TYPE),
    api_url: z.url({ message: validationMessagesMap.validationErrorURL }),
    api_key: z.string(),
});

// Cash/Card API components don't carry any extra config — they're just a
// per-paystation health subject for the backend's cash/card APIs.
const apiHealthDataSchema = z.object({}).optional();

const ticketPrinterPayload = z
    .object({
        align: z.enum(ALIGN),
        type: z.enum(TICKET_PRINTER_ROW_TYPE),
        text: z.string().optional(),
    })
    .superRefine((data, ctx) => {
        if (data.type === TICKET_PRINTER_ROW_TYPE.TEXT && !data.text) {
            ctx.addIssue({
                path: ['text'],
                message: validationMessagesMap.validationErrorRequired,
                code: 'custom',
            });
        }
    });

const ticketPrinterDataSchema = z.object({
    channel: z.number().int().min(0),
    topic_code: z.string().optional(),
    payload: z.array(ticketPrinterPayload).optional(),
});

const systemComponentUnion = z.discriminatedUnion('component', [
    z.object({
        component: z.literal(SYSTEM_COMPONENT.LPR_CAMERA),
        data: cameraComponentDataSchema,
    }),
    z.object({
        component: z.literal(SYSTEM_COMPONENT.GATEWAY_COMMUNICATOR),
        data: gatewayCommunicatorDataSchema,
    }),
    z.object({
        component: z.literal(SYSTEM_COMPONENT.GATEWAY_DISPLAY),
        data: gatewayDisplayDataSchema,
    }),
    z.object({
        component: z.literal(SYSTEM_COMPONENT.FISCAL_DEVICE),
        data: fiscalDeviceDataSchema,
    }),
    z.object({
        component: z.literal(SYSTEM_COMPONENT.SENSOR_READER_COMMUNICATOR),
        data: sensorReaderCommunicatorDataSchema,
    }),
    z.object({
        component: z.literal(SYSTEM_COMPONENT.ACCESS_KEY_READER_COMMUNICATOR),
        data: communicatorDataSchema,
    }),
    z.object({
        component: z.literal(SYSTEM_COMPONENT.CASH_API),
        data: apiHealthDataSchema,
    }),
    z.object({
        component: z.literal(SYSTEM_COMPONENT.CARD_API),
        data: apiHealthDataSchema,
    }),
    z.object({
        component: z.literal(SYSTEM_COMPONENT.TICKET_PRINTER),
        data: ticketPrinterDataSchema,
    }),
]);

const baseSystemComponentFormSchema = z.object({
    component_type: z.string(),
    component: z.enum([
        SYSTEM_COMPONENT.LPR_CAMERA,
        SYSTEM_COMPONENT.GATEWAY_COMMUNICATOR,
        SYSTEM_COMPONENT.ACCESS_KEY_READER_COMMUNICATOR,
        SYSTEM_COMPONENT.SENSOR_READER_COMMUNICATOR,
        SYSTEM_COMPONENT.CASH_API,
        SYSTEM_COMPONENT.CARD_API,
        SYSTEM_COMPONENT.GATEWAY_DISPLAY,
        SYSTEM_COMPONENT.FISCAL_DEVICE,
        SYSTEM_COMPONENT.TICKET_PRINTER,
    ]),
    name: z.string(),
    code: z.string(),
    zone: z.string().optional(),
    gateway: z.string(),
    pay_station: z.string().optional(),
    logo: z.instanceof(File).nullable().optional(),
});
export const systemComponentFormSchema = baseSystemComponentFormSchema
    .and(systemComponentUnion)
    .superRefine((values, ctx) => {
        if (COMPONENTS_WITHOUT_ZONE.includes(values.component)) return;

        if (!values.zone) {
            ctx.addIssue({
                path: ['zone'],
                message: validationMessagesMap.validationErrorRequired,
                code: 'custom',
            });
        }
    });
